import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { localeForCountry, fmtMoney } from '../utils/locale';
import {
  Scale, Info, Loader2, MessageSquarePlus, Trash2, X, EyeOff,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || '')
  .split(',').map((e) => e.trim().toLowerCase()).filter(Boolean);

const isAdmin = (user) =>
  !!user && (user.role === 'admin' || (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())));

const MethodologyModal = ({ data, onClose, L, country }) => {
  const cs = L.currencySymbol;
  const fmtInt = (n) =>
    new Intl.NumberFormat(L.locale, { maximumFractionDigits: 0 }).format(Number(n) || 0);
  return (
  <div
    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="calibration-methodology-modal"
  >
    <div
      className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold">How this is calculated</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="space-y-3 text-sm text-gray-700">
        <p>
          We compared this property&apos;s <strong>{cs}/m²</strong> against <strong>{data.sample_size} other listings</strong>{' '}
          that match all of:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Same property type: <strong>{data.criteria.property_type}</strong></li>
          <li>Same bedrooms: <strong>{data.criteria.bedrooms}</strong></li>
          <li>Within <strong>{data.criteria.radius_km} km</strong> of this address</li>
          <li>Listed within the last <strong>{data.criteria.window_days} days</strong></li>
          <li>Floor area within ±<strong>{data.criteria.size_tolerance_pct}%</strong> of this listing&apos;s {data.this_size_sqm} m²</li>
        </ul>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{cs}/m² range across the {data.sample_size} comparables</div>
          <div className="flex justify-between"><span>25th percentile</span><strong>{cs}{fmtInt(data.p25_per_sqm)}/m²</strong></div>
          <div className="flex justify-between text-[#7042cc]"><span>Median (50th)</span><strong>{cs}{fmtInt(data.median_per_sqm)}/m²</strong></div>
          <div className="flex justify-between"><span>75th percentile</span><strong>{cs}{fmtInt(data.p75_per_sqm)}/m²</strong></div>
          <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
            <span>This listing</span><strong>{cs}{fmtInt(data.this_price_per_sqm)}/m²</strong>
          </div>
          <div className="text-xs text-gray-500 pt-1">
            At the median {cs}/m², a {data.this_size_sqm} m² home would imply ~{fmtMoney(data.median_implied_price, country)}.
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          <strong>Limitations:</strong> {cs}/m² treats every square metre as equal — it doesn&apos;t account for condition,
          finish, garden, {L.energyLabel}, renovation status, or specific room layouts. Two homes can match all criteria above and
          still differ materially. This is a market-context guide, not a verdict.
        </p>
      </div>
    </div>
  </div>
  );
};

const AgentResponseEditor = ({ propertyId, existing, canEdit, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(existing || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (text.trim().length < 1) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/properties/${propertyId}/calibration/response`,
        { response: text.trim() }, { headers: authHeaders() });
      toast({ title: 'Response saved' });
      setEditing(false);
      onChange?.(text.trim());
    } catch (e) {
      toast({ title: 'Could not save', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remove your response?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/api/properties/${propertyId}/calibration/response`, { headers: authHeaders() });
      onChange?.(null);
    } catch (e) {
      toast({ title: 'Could not remove', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (!editing && existing) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 text-sm" data-testid="calibration-agent-response">
        <div className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">
          Note from listing agent
        </div>
        <div className="text-gray-700">{existing}</div>
        {canEdit && (
          <div className="flex gap-2 mt-2 text-xs">
            <button onClick={() => { setText(existing); setEditing(true); }} className="text-amber-700 hover:underline">Edit</button>
            <button onClick={remove} disabled={saving} className="text-rose-600 hover:underline">Remove</button>
          </div>
        )}
      </div>
    );
  }

  if (!editing && !existing && canEdit) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-xs text-[#7042cc] hover:underline inline-flex items-center gap-1 mt-2"
        data-testid="calibration-add-response-btn"
      >
        <MessageSquarePlus className="w-3 h-3" /> Add a response to this context
      </button>
    );
  }

  if (editing) {
    return (
      <div className="mt-2 space-y-2" data-testid="calibration-response-editor">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="One sentence, e.g. 'Recently renovated to A-rated standard, no comparable stock in area.'"
          rows={2}
          maxLength={240}
          data-testid="calibration-response-textarea"
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(existing || ''); }}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#7042cc] hover:bg-[#5b35a8]" data-testid="calibration-response-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default function PricingCalibrationBadge({ propertyId, property }) {
  const { user, isAuthenticated } = useAuth();
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  const load = async () => {
    if (!propertyId) return;
    try {
      const r = await axios.get(`${API}/api/properties/${propertyId}/calibration`,
        { headers: isAuthenticated ? authHeaders() : {} });
      setResp(r.data);
    } catch {
      setResp({ visible: false, reason: 'error' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [propertyId, isAuthenticated]);

  if (loading || !resp) return null;

  // Not visible to public, and viewer isn't admin → render nothing
  if (!resp.visible && resp.reason === 'admin_preview_only') return null;
  if (!resp.visible) return null;

  const d = resp.data;
  const adminPreview = resp.admin_preview;
  // Owner / agent / admin can edit the response. We'll detect agent/owner via /can-edit on passport endpoint.
  const viewerIsAdmin = isAdmin(user);
  const ownerCanEdit = !!property && (property.userId === user?.id || property.userId === user?._id);
  const canEdit = viewerIsAdmin || ownerCanEdit;

  const pct = d.pct_vs_median;
  const direction = pct > 0 ? 'above' : pct < 0 ? 'below' : 'at';
  const country = property?.location?.country || property?.country;
  const L = localeForCountry(country);
  const cs = L.currencySymbol;
  const fmtInt = (n) =>
    new Intl.NumberFormat(L.locale, { maximumFractionDigits: 0 }).format(Number(n) || 0);
  const bandColor =
    d.band === 'above_band' ? 'bg-amber-50 border-amber-200 text-amber-900'
      : d.band === 'below_band' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
        : 'bg-gray-50 border-gray-200 text-gray-800';

  return (
    <>
      <Card data-testid="pricing-calibration-badge" className={`${bandColor} border-l-4`}>
        <CardContent className="p-4">
          {adminPreview && (
            <div className="flex items-center gap-1 text-xs font-semibold mb-2 text-[#7042cc] uppercase tracking-wider" data-testid="calibration-admin-preview-tag">
              <EyeOff className="w-3 h-3" /> Admin preview · not public yet
            </div>
          )}
          <div className="flex items-start gap-3">
            <Scale className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold">Market context</div>
              <div className="text-sm mt-1">
                {Math.abs(pct) < 0.5 ? (
                  <>At the 6-month median {cs}/m² for similar {d.criteria.bedrooms}-bed {d.criteria.property_type}s within {d.criteria.radius_km} km.</>
                ) : (
                  <>
                    <strong>{Math.abs(pct)}% {direction}</strong> the 6-month median {cs}/m² ({cs}{fmtInt(d.median_per_sqm)}/m²) for
                    similar {d.criteria.bedrooms}-bed {d.criteria.property_type}s within {d.criteria.radius_km} km.
                  </>
                )}
              </div>
              <div className="text-xs mt-1.5 opacity-75">
                This listing: <strong>{cs}{fmtInt(d.this_price_per_sqm)}/m²</strong> ({d.this_size_sqm} m²) ·{' '}
                Range across {d.sample_size} similar homes: {cs}{fmtInt(d.p25_per_sqm)} – {cs}{fmtInt(d.p75_per_sqm)}/m²
              </div>
              <button
                onClick={() => setMethodologyOpen(true)}
                className="text-xs underline mt-2 inline-flex items-center gap-1"
                data-testid="calibration-methodology-btn"
              >
                <Info className="w-3 h-3" /> How this is calculated
              </button>
            </div>
          </div>
          <div className="mt-3">
            <AgentResponseEditor
              propertyId={propertyId}
              existing={d.agent_response}
              canEdit={canEdit}
              onChange={(v) => setResp((r) => ({ ...r, data: { ...r.data, agent_response: v } }))}
            />
          </div>
        </CardContent>
      </Card>
      {methodologyOpen && <MethodologyModal data={d} onClose={() => setMethodologyOpen(false)} L={L} country={country} />}
    </>
  );
}
